import { useState } from 'react';

export interface GraphUser {
  id: string;
  displayName: string;
  givenName?: string;
  surname?: string;
  mail?: string;
  userPrincipalName: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  manager?: {
    id: string;
    displayName: string;
    mail?: string;
  };
}

export interface HardwareRequestFormData {
  // Step 0: User Selection
  selectedUserId: string;
  selectedUserName: string;
  selectedUserEmail: string;
  selectedUserPrincipalName: string;
  managerName: string;
  managerEmail: string;

  // Step 1: Hardware Selection
  mainHardware: string[];
  peripherals: string[];
  monitorQuantity: number;
  justification: string;

  // Wizard state
  currentStep: number;
}

const initialFormData: HardwareRequestFormData = {
  selectedUserId: '',
  selectedUserName: '',
  selectedUserEmail: '',
  selectedUserPrincipalName: '',
  managerName: '',
  managerEmail: '',
  mainHardware: [],
  peripherals: [],
  monitorQuantity: 0,
  justification: '',
  currentStep: 0,
};

export function useHardwareRequestForm() {
  const [formData, setFormData] = useState<HardwareRequestFormData>(initialFormData);

  // Update form data
  const updateFormData = (updates: Partial<HardwareRequestFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // Set user data from API
  const setUserData = (user: GraphUser) => {
    setFormData({
      ...initialFormData,
      selectedUserId: user.id,
      selectedUserName: user.displayName,
      selectedUserEmail: user.mail || user.userPrincipalName,
      selectedUserPrincipalName: user.userPrincipalName,
      managerName: user.manager?.displayName || '',
      managerEmail: user.manager?.mail || '',
      currentStep: 0,
    });
  };

  // Toggle main hardware selection
  const toggleMainHardware = (item: string) => {
    setFormData((prev) => {
      const isSelected = prev.mainHardware.includes(item);
      return {
        ...prev,
        mainHardware: isSelected
          ? prev.mainHardware.filter((hw) => hw !== item)
          : [...prev.mainHardware, item],
      };
    });
  };

  // Toggle peripheral selection
  const togglePeripheral = (item: string) => {
    setFormData((prev) => {
      const isSelected = prev.peripherals.includes(item);
      return {
        ...prev,
        peripherals: isSelected
          ? prev.peripherals.filter((p) => p !== item)
          : [...prev.peripherals, item],
      };
    });
  };

  // Set monitor quantity
  const setMonitorQuantity = (quantity: number) => {
    setFormData((prev) => ({ ...prev, monitorQuantity: quantity }));
  };

  // Set justification
  const setJustification = (text: string) => {
    setFormData((prev) => ({ ...prev, justification: text }));
  };

  // Validate current step
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // User Selection
        return formData.selectedUserId !== '';
      case 1: // Hardware Selection
        // At least one hardware item selected OR monitors selected
        const hasHardware =
          formData.mainHardware.length > 0 ||
          formData.peripherals.length > 0 ||
          formData.monitorQuantity > 0;
        // Justification must be at least 10 characters
        const hasValidJustification = formData.justification.trim().length >= 10;
        return hasHardware && hasValidJustification;
      case 2: // Review - always valid
        return true;
      default:
        return false;
    }
  };

  // Navigation functions
  const nextStep = () => {
    const isValid = validateStep(formData.currentStep);
    if (isValid && formData.currentStep < 2) {
      setFormData((prev) => ({ ...prev, currentStep: prev.currentStep + 1 }));
    }
  };

  const prevStep = () => {
    if (formData.currentStep > 0) {
      setFormData((prev) => ({ ...prev, currentStep: prev.currentStep - 1 }));
    }
  };

  const goToStep = (step: number) => {
    if (step >= 0 && step <= 2) {
      setFormData((prev) => ({ ...prev, currentStep: step }));
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData(initialFormData);
  };

  // Get total items selected
  const getTotalItemsSelected = (): number => {
    return formData.mainHardware.length + formData.peripherals.length + (formData.monitorQuantity > 0 ? 1 : 0);
  };

  return {
    formData,
    updateFormData,
    setUserData,
    toggleMainHardware,
    togglePeripheral,
    setMonitorQuantity,
    setJustification,
    validateStep,
    nextStep,
    prevStep,
    goToStep,
    resetForm,
    getTotalItemsSelected,
  };
}
